import React, { useEffect, useMemo, useState } from "react";
import api from "../../../../config/URL";
import { Link } from "react-router-dom";
import BatchTimeEdit from "./BatchTimeEdit";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import {
  createTheme,
  IconButton,
  Menu,
  MenuItem,
  ThemeProvider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { toast } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import { exportVisibleColumnsToCSV } from "../../../../components/common/ExportCsv";

const BatchTime = () => {
  const [datas, setDatas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewData, setViewData] = useState(null);

  /* -------------------- ROLE MATRIX -------------------- */
  const storedScreens = JSON.parse(localStorage.getItem("screens") || "{}");

  const canIndex = Boolean(storedScreens?.batchtimeSettingIndex);
  const canRead = Boolean(storedScreens?.batchtimeSettingRead);
  const canUpdate = Boolean(storedScreens?.batchtimeSettingUpdate);

  const showActionColumn = canUpdate;

  const handleRowView = async (id) => {
    try {
      const response = await api.get(`/getBatchForSingleDay/${id}`);
      setViewData(response.data);
      setViewOpen(true);
    } catch {
      toast.error("Failed to fetch batch details");
    }
  };

  const handleViewClose = () => {
    setViewOpen(false);
    setViewData(null);
  };

  const getData = async () => {
    try {
      const response = await api.get("/getAllBatches");
      setDatas(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  /* -------------------- COLUMNS -------------------- */
  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorFn: (row, index) => index + 1,
        header: "S.NO",
        size: 40,
        Cell: ({ cell }) => (
          <span style={{ textAlign: "center" }}>{cell.getValue()}</span>
        ),
      },
      {
        accessorKey: "batchDay",
        header: "Batch Day",
      },
      {
        accessorKey: "batchTimes",
        header: "Class Timeslot",
        Cell: ({ cell }) => <span>{formatBatchTimes(cell.getValue())}</span>,
      },
      { accessorKey: "createdBy", header: "Created By" },
      {
        accessorKey: "createdAt",
        header: "Created On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10),
      },
      {
        accessorKey: "updatedAt",
        header: "Updated On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10) || "",
      },
      {
        accessorKey: "updatedBy",
        header: "Updated By",
        Cell: ({ cell }) => cell.getValue() || "",
      },
    ];

    if (!showActionColumn) return baseColumns;

    return [
      baseColumns[0], // S.NO
      {
        accessorKey: "batchId",
        header: "",
        size: 20,
        enableSorting: false,
        Cell: ({ cell }) => (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchor(e.currentTarget);
              setSelectedId(cell.getValue());
            }}
          >
            <MoreVertIcon />
          </IconButton>
        ),
      },
      ...baseColumns.slice(1),
    ];
  }, [showActionColumn]);

  /* -------------------- TABLE INSTANCE -------------------- */
  const table = useMaterialReactTable({
    columns,
    data: datas,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    initialState: {
      pagination: { pageSize: 50, pageIndex: 0 },
      columnVisibility: {
        createdBy: false,
        createdAt: false,
        updatedBy: false,
        updatedAt: false,
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: canRead ? () => handleRowView(row.original.batchId) : undefined,
      style: { cursor: canRead ? "pointer" : "default" },
    }),
    renderTopToolbarCustomActions: ({ table }) => (
      <button
        className="btn btn-sm btn-border"
        onClick={() => exportVisibleColumnsToCSV(table, datas, "BatchTime.csv")}
      >
        Export CSV
      </button>
    ),
  });

  /* -------------------- THEME -------------------- */
  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: "#535454 !important",
            backgroundColor: "#e6edf7 !important",
            fontWeight: "400 !important",
            fontSize: "13px !important",
            textAlign: "center !important",
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            "&.Mui-disabled .MuiSwitch-track": {
              backgroundColor: "#f5e1d0",
              opacity: 1,
            },
            "&.Mui-disabled .MuiSwitch-thumb": {
              color: "#eb862a",
            },
          },
          track: { backgroundColor: "#e0e0e0" },
          thumb: { color: "#eb862a" },
          switchBase: {
            "&.Mui-checked": { color: "#eb862a" },
            "&.Mui-checked + .MuiSwitch-track": {
              backgroundColor: "#eb862a",
            },
          },
        },
      },
    },
  });

  const handleMenuClose = () => setMenuAnchor(null);

  function formatBatchTimes(batchTimes) {
    if (!batchTimes) return "";
    const batchTimesString = String(batchTimes);

    const times = batchTimesString.includes(",")
      ? batchTimesString.split(",").map((t) => t.trim())
      : batchTimesString.match(/.{1,5}/g) || [];

    const convertTo12Hour = (time) => {
      const [hour, minute] = time.split(":").map(Number);
      if (isNaN(hour) || isNaN(minute)) return time;
      const period = hour >= 12 ? "PM" : "AM";
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minute.toString().padStart(2, "0")} ${period}`;
    };

    return times.map(convertTo12Hour).join(", ");
  }

  return (
    <div className="container-fluid center">
      <ol className="breadcrumb px-1 mb-2" style={{ listStyle: "none" }}>
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          &nbsp;Settings<span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          &nbsp;Master Settings
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="breadcrumb-item active">&nbsp;Class Timeslot</li>
      </ol>

      <div className="card">
        <div
          className="d-flex justify-content-between align-items-center p-1"
          style={{ background: "#f5f7f9" }}
        >
          <div className="d-flex align-items-center">
            <div className="dot active"></div>
            <span className="ms-2 text-muted">
              This database shows the list of{" "}
              <span className="bold" style={{ color: "#287f71" }}>
                Class Timeslot
              </span>
            </span>
          </div>
        </div>

        {loading ? (
          <div className="loader-container">
            <div className="loading">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        ) : (
          <>
            <ThemeProvider theme={theme}>
              <MaterialReactTable table={table} />
            </ThemeProvider>
            {showActionColumn && (
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
              >
                {canUpdate && (
                  <MenuItem onClick={(e) => e.stopPropagation()}>
                    <BatchTimeEdit
                      onSuccess={getData}
                      id={selectedId}
                      handleMenuClose={handleMenuClose}
                    />
                  </MenuItem>
                )}
              </Menu>
            )}

            {/* VIEW MODAL */}
            <Dialog
              open={viewOpen}
              onClose={handleViewClose}
              maxWidth={false}
              PaperProps={{ style: { width: "350px" } }}
            >
              <DialogTitle>
                Class Timeslot Details
                <IconButton
                  onClick={handleViewClose}
                  style={{ position: "absolute", right: 8, top: 8 }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>

              <DialogContent>
                {viewData && (
                  <>
                    <p className="text-muted mb-1">Day</p>
                    <p className="fw-medium">{viewData.batchDay}</p>

                    <p className="fw-medium mt-3 mb-2">Batch Details</p>

                    {Array.isArray(viewData.batchTimes) &&
                      viewData.batchTimes.map((time, index) => (
                        <div key={index} className="d-flex mb-2">
                          <div style={{ width: "50%" }}>
                            {formatBatchTimes(time)}
                          </div>
                          <div style={{ width: "50%", textAlign: "right" }}>
                            {viewData.totalStudents?.[index] ?? "—"}
                          </div>
                        </div>
                      ))}
                  </>
                )}
              </DialogContent>

              <DialogActions>
                <button
                  className="btn btn-border btn-sm"
                  onClick={handleViewClose}
                >
                  Close
                </button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </div>
    </div>
  );
};

export default BatchTime;
